import { Stack, styled } from '@ethings/ui-toolkit';
import { SxProps } from '@mui/system';
import { FC, type JSX } from 'react';
import { useSmallScreen } from '../../hooks';
import { IfFlag } from '../flags';
import { MenuIcon } from '../icons';
import { MenuItem } from './menu-item';
import { MenuItemProps } from './menu-item.types';

const GroupStack = styled(Stack)(({ theme }) => ({
  '& >a:last-of-type:not(:only-of-type) .MenuItem-root': {
    marginBottom: theme.spacing(1),
  },
}));

interface Props {
  'data-testid'?: string;
  hasPermissions?: boolean;
  icon?: JSX.Element;
  menuItems: MenuItemProps[];
  open?: boolean;
  path?: string;
  sx?: SxProps;
  title: string;
  className?: string | undefined;
  target?: '_self' | '_blank';
}

export const MenuItemGroup: FC<Props> = ({ hasPermissions = true, icon, menuItems, open, path, sx, target, title, className, ...props }) => {
  const smallScreen = useSmallScreen();
  const sxMl = { height: 36, '& .icon': { minWidth: 32, width: 32, '& svg': { width: 16, height: 16 } }, ...(smallScreen ? { pl: 6.25 } : { pl: open ? 6.25 : 2.5 }), ...sx };
  const keepAsDisabled = !hasPermissions && menuItems.some((item) => item.hasPermissions);

  if (!hasPermissions && !keepAsDisabled) return <></>;

  return (
    <GroupStack
      data-testid={props['data-testid']}
      direction="column"
      className={'MenuItemGroup-root'}
      borderBottom={1} borderColor="divider">
      <MenuItem
        hasPermissions={hasPermissions}
        icon={<MenuIcon icon={icon} />}
        keepAsDisabled={keepAsDisabled}
        path={path}
        open={open}
        target={target}
        sx={{
          fontSize: '1rem',
          '& .icon': { minWidth: 32, width: 32, marginLeft: 2.5 },
          ...(path ? { ...sx, cursor: 'pointer' } : { cursor: 'default' }),
        }}
        text={title}
        className={className}
      />
      {menuItems.map((item, i) =>
        <IfFlag key={i} flag={item.flag}>
          <MenuItem
            data-testid={item['data-testid']}
            hasPermissions={item.hasPermissions}
            icon={item.icon}
            path={item.path}
            open={open}
            subText={item.subText}
            target={item.target}
            sx={{
              fontSize: '0.9rem',
              transition: '250ms padding-left ease',
              ...sxMl,
            }}
            text={item.text}
            className={item.className}
          />
        </IfFlag>,
      )}
    </GroupStack>
  );
};
